import moment from "moment";
import { IconButton } from "@mui/material";
import { Theme } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RadioButtonUnchecked, RadioButtonChecked, Close, Search, AddCircleOutline } from "@mui/icons-material";

import Box from "../../Box";
import Modal from "../../Modal";
import Avatar from "../../Avatar";
import FormField from "../../FormField";
import Typography from "../../Typography";
import Helpers from "../../../commons/helpers";
import useStrings from "../../../hooks/useStrings";
import Resources from "../../../constants/resources";

import { IManageUser } from "../../../commons/interfaces";
import { ItemType, avatarAndName, itemBox, typography } from "../styles";
import { useCommonComponentContext } from "../../../providers/CommonComponentProvider";

interface INewUserData {
    email: string;
    name: string;
}

type IError = { [k in keyof INewUserData]: string };

interface IProps {
    data: IManageUser[];
    selectedData: IManageUser[];

    onAddNewData: (data: IManageUser[]) => void;
    onSelected: (data: IManageUser[]) => void;
}

const AddNewUserTab: React.FC<IProps> = ({ onSelected, onAddNewData, selectedData, ...props }: IProps) => {
    const strings = useStrings();
    const { onError } = useCommonComponentContext();

    const [search, setSearch] = useState<string>("");
    const [selected, setSelected] = useState<string[]>([]);
    const [data, setData] = useState<IManageUser[]>([]);
    const [visibled, setVisibled] = useState<boolean>(false);

    useEffect(() => {
        setData(props.data);
    }, [props.data]);

    useEffect(() => {
        if (!Helpers.isNullOrEmpty(selectedData)) {
            setSelected((selectedData || []).map((item) => item?.id));
        }
    }, [selectedData]);

    const onSearch = () => { };

    const handleSelectItem = (id: string, selectedItem: IManageUser) => {
        if (selected.includes(id)) {
            setSelected(selected.filter((item) => item !== id));
            onSelected(selectedData.filter((item) => item.id !== selectedItem.id));
        } else {
            setSelected((prev) => [...prev, id]);
            onSelected([...selectedData, selectedItem]);
        }
    };

    const filteredData = useMemo(() => (search ? data.filter((item) => item.fullName.includes(search)) || [] : data), [search, data]);

    const onClose = () => setVisibled(false);

    const onAction = (item: IManageUser) => {
        if (!Helpers.isNullOrEmpty(data.filter((child) => child.id === item.id))) {
            onError(strings.Common.EMAIL_ALREADY_EXISTS);
            return;
        }
        onAddNewData([...data, item]);
        onSelected([...selectedData, item]);

        setData((prev) => [...prev, item]);
        setSelected((prev) => [...prev, item.id]);
    };

    const renderItem = useCallback(
        (item: IManageUser) => {
            let isSelected = selected.includes(item.id);
            return (
                <Box
                    key={item.id}
                    sx={(theme) => itemBox(theme, { isSelected, type: ItemType.User })}
                    onClick={() => handleSelectItem(item.id, item)}
                >
                    <Box sx={avatarAndName}>
                        <Avatar
                            size="sm"
                            text={item.fullName}
                            src={item.avatarUrl}
                            style={{ border: "1px #c3c3c3 solid" }}
                        />
                        <Box>
                            <Typography
                                sx={(theme: Theme) =>
                                    typography(theme, {
                                        fontSize: "0.8rem",
                                        fontWeight: "bold",
                                    })
                                }
                            >
                                {item.fullName}
                            </Typography>
                            <Typography sx={typography}>{item.email}</Typography>
                        </Box>
                    </Box>
                    <Box>
                        {isSelected ? (
                            <RadioButtonChecked color="info" sx={{ fontSize: "1rem" }} />
                        ) : (
                            <RadioButtonUnchecked sx={{ fontSize: "1rem" }} />
                        )}
                    </Box>
                </Box>
            );
        },
        [handleSelectItem, selected]
    );

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Box sx={{ width: "90%" }}>
                    <FormField
                        fullWidth
                        size="small"
                        defaultValue={search || ""}
                        placeholder={`${strings.Common.SEARCH}...`}
                        onBlur={(value: any) => setSearch(value)}
                        onKeyPress={(e: any) => {
                            if (e.key === "Enter") {
                                setSearch(e.target.value);
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <IconButton color="info" onClick={() => onSearch()}>
                                    <Search />
                                </IconButton>
                            ),
                            endAdornment: search && (
                                <IconButton
                                    color="secondary"
                                    onClick={() => {
                                        setSearch(undefined);
                                    }}
                                >
                                    <Close />
                                </IconButton>
                            ),
                        }}
                    />
                </Box>
                <IconButton color="secondary" onClick={() => setVisibled(true)}>
                    <AddCircleOutline />
                </IconButton>
            </Box>
            <Box
                sx={{
                    mt: 1,
                    display: "grid",
                    gap: "0.5rem",
                    overflowY: "scroll",
                }}
            >
                {filteredData.map(renderItem)}
            </Box>
            <AddNewModal visibled={visibled} onClose={onClose} onAction={onAction} />
        </>
    );
};

interface IPropsModal {
    visibled: boolean;
    onClose: () => void;
    onAction: (item: IManageUser) => void;
}

const AddNewModal = ({ visibled, onClose, onAction }: IPropsModal) => {
    const strings = useStrings();
    const { onError } = useCommonComponentContext();
    const [addNewUserData, setAddNewUserData] = useState<INewUserData>({
        email: "",
        name: "",
    });

    const [error, setError] = useState<IError>({
        email: "",
        name: "",
    });

    const onChangeValue = (newValue: string, key: keyof INewUserData) => {
        setAddNewUserData((prev) => ({ ...prev, [key]: newValue }));
        if (!Helpers.isNullOrEmpty(error[key])) {
            setError((prev) => ({ ...prev, [key]: "" }));
        }
    };

    const handleCheckValidate = () => {
        let keys = ["email"];
        let newError = { ...error };
        let checked = true;
        keys.forEach((key) => {
            let objKey = key as keyof INewUserData;
            if (Helpers.isNullOrEmpty(addNewUserData[objKey])) {
                newError[objKey] = strings.Common.REQUIRED;
                checked = false;
            } else if (objKey === "email" && !Helpers.isValidEmail(addNewUserData[objKey])) {
                newError[objKey] = strings.Common.INVALID_EMAIL;
                checked = false;
            }
        });
        if (!checked) {
            setError(newError);
        }
        return checked;
    };

    const handleAddNewItem = async () => {
        try {
            if (!handleCheckValidate()) return;
            let newItem: IManageUser = {
                userName: "0763960876",
                email: addNewUserData.email,
                phoneNumber: "+84-45544444",
                avatarId: "",
                avatarUrl: "",
                fullName: addNewUserData.name,
                birthDate: "944240400",
                gender: 2,
                introduction: null,
                identityId: moment().unix().toString(),
                location: "VN",
                id: moment().unix().toString(),
                status: 1,
                createTime: "1690356261",
                createUser: "464713852743696384",
                updateTime: "1695807563",
                updateUser: "471940823827607552",
            };

            onAction(newItem);
            setAddNewUserData({
                email: "",
                name: "",
            });
        } catch (error) {
            const e = Helpers.renderExceptionError(error);
            onError(e);
        }
    };

    return (
        <Modal
            fullWidth
            maxWidth="xs"
            hasActionButton
            onAction={handleAddNewItem}
            visible={visibled}
            onClose={onClose}
            title={strings.Common.ADD_NEW}
        >
            <Box
                sx={{
                    display: "grid",
                    gap: "0.5rem",
                }}
            >
                <FormField
                    key={addNewUserData?.email}
                    required
                    fullWidth
                    size="small"
                    label={strings.Organization.EMAIL}
                    placeholder={strings.Organization.ENTER_EMAIL}
                    errorMessage={error.email}
                    defaultValue={addNewUserData?.email}
                    onBlur={(value: any) => onChangeValue(value, "email")}
                />

                {/* <FormField
                    required
                    fullWidth
                    size="small"
                    label={strings.STAFF.NAME}
                    placeholder={strings.STAFF.ENTER_NAME}
                    errorMessage={error.name}
                    onBlur={(value: any) => onChangeValue(value, "name")}
                /> */}
            </Box>
        </Modal>
    );
};

export default AddNewUserTab;
